import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Эндонимы («как язык называет себя сам») — ДАННЫЕ, а не литералы кода:
// гейт check-i18n запрещает кириллицу в строковых литералах `src/**`.
import endonyms from './locales/languages.json';

/**
 * S4-35 (I18N-PANEL): конфигурация i18n панели.
 *
 * Три решения, которые держат остальную локализацию:
 *  1. Каталог `languages` — ЕДИНСТВЕННОЕ место, где язык интерфейса
 *     отображается в Intl-локаль и цепочку фолбэка (I4).
 *     Потребители форматируют через `@/lib/locale-format`, сами не маппят.
 *  2. Ресурсы собираются из каталогов `locales/<lang>/<namespace>.json`:
 *     файл = ключ верхнего уровня (`settings.json` → `t('settings.…')`),
 *     его содержимое — поддерево этого ключа. Добавление языка = новый
 *     каталог + запись в каталоге ниже + эндоним в `languages.json`.
 *  3. Отсутствующий перевод падает по цепочке `es → en → ru`, пустая строка
 *     считается отсутствием (`returnEmptyString: false`) — пользователь
 *     никогда не видит ни пустоту, ни сырой ключ (I3).
 */

/** Запись каталога UI-языков. */
export interface LanguageEntry {
  /** Код ISO 639-1 — он же имя каталога `locales/<code>/`. */
  code: string;
  /** Английское имя (для логов/aria, не для показа). */
  name: string;
  /** Эндоним — так язык подписывается в переключателе. */
  nativeName: string;
  /** Intl-локаль для дат/чисел/валют. */
  intl: string;
  /** Цепочка фолбэка перевода (без самого языка). */
  fallback: readonly string[];
}

/**
 * Каталог UI-языков — языки, на которые ПЕРЕВЕДЁН интерфейс панели.
 *
 * ⚠ Не путать с `PROFILE_LANGUAGE_CODES` (`@/api/locale.api`): там языки,
 * на которых отвечает АССИСТЕНТ. Профиль может нести язык, на который
 * интерфейс не переведён, — см. `resolveUiLanguage`.
 */
export const languages = [
  {
    code: 'ru',
    name: 'Russian',
    nativeName: endonyms.ru,
    intl: 'ru-RU',
    fallback: [],
  },
  {
    code: 'en',
    name: 'English',
    nativeName: endonyms.en,
    intl: 'en-US',
    fallback: ['ru'],
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: endonyms.es,
    intl: 'es-ES',
    fallback: ['en', 'ru'],
  },
] as const satisfies readonly LanguageEntry[];

/** Код языка из каталога UI-языков. */
export type UiLanguage = (typeof languages)[number]['code'];

/**
 * Язык по умолчанию: референсная локаль (гейт check-i18n сверяет с ней
 * t()-вызовы) и последнее звено любой цепочки фолбэка.
 */
export const DEFAULT_LANGUAGE: UiLanguage = 'ru';

/**
 * Язык UI для профиля, чей язык интерфейс не знает (ассистент говорит
 * на нём, а перевода панели нет): английский — самый вероятный второй язык.
 */
export const PROFILE_FALLBACK_LANGUAGE: UiLanguage = 'en';

/**
 * Ресурсы: `locales/<lang>/<namespace>.json` → `resources[lang].translation[namespace]`.
 * Всё — в одном namespace `translation`, поэтому ключи в коде не зависят
 * от раскладки файлов.
 */
const localeModules = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', {
  eager: true,
  import: 'default',
});

export const resources: Record<string, { translation: Record<string, unknown> }> = {};
for (const [path, tree] of Object.entries(localeModules)) {
  // path = './locales/<lang>/<namespace>.json'
  const [, , lang, file] = path.split('/');
  const namespace = file.replace(/\.json$/, '');
  (resources[lang] ??= { translation: {} }).translation[namespace] = tree;
}

const fallbackLng: Record<string, string[]> = { default: [DEFAULT_LANGUAGE] };
for (const entry of languages) {
  if (entry.fallback.length > 0) fallbackLng[entry.code] = [...entry.fallback];
}

/**
 * Язык каталога по произвольному коду (`es-MX` → `es`, `EN` → `en`).
 *
 * @contract Сопоставление кода с каталогом UI-языков по первичному подтегу.
 * @requires code — BCP 47 / ISO 639 код или пусто.
 * @ensures Возвращает код из каталога или `null`, если язык не переведён.
 * @ensures Никогда не бросает.
 */
export function matchUiLanguage(code: string | null | undefined): UiLanguage | null {
  if (!code) return null;
  const primary = code.trim().toLowerCase().split(/[-_]/)[0];
  const entry = languages.find((l) => l.code === primary);
  return entry ? entry.code : null;
}

/**
 * Язык интерфейса для языка ПРОФИЛЯ (`user_profile.language`).
 *
 * @contract I1 — язык UI залогиненного выводится из профиля.
 * @requires profileLanguage — непустое значение оси `language` профиля.
 * @ensures Язык из каталога UI, если интерфейс на него переведён;
 *   иначе `PROFILE_FALLBACK_LANGUAGE` (ассистент говорит на языке, на
 *   который панель не переведена, — показываем английский, а не русский).
 * @business_impact Неверное сопоставление = интерфейс на чужом языке у
 *   человека, явно выбравшего свой.
 */
export function resolveUiLanguage(profileLanguage: string): UiLanguage {
  return matchUiLanguage(profileLanguage) ?? PROFILE_FALLBACK_LANGUAGE;
}

/**
 * Язык интерфейса экземпляра i18next (всегда из каталога).
 *
 * @ensures `resolvedLanguage` → `language` → `DEFAULT_LANGUAGE`.
 */
export function uiLanguageOf(instance: {
  resolvedLanguage?: string;
  language?: string;
}): UiLanguage {
  return (
    matchUiLanguage(instance.resolvedLanguage) ??
    matchUiLanguage(instance.language) ??
    DEFAULT_LANGUAGE
  );
}

/** Текущий язык интерфейса (вне React; в компонентах — `useUiLanguage()`). */
export function currentUiLanguage(): UiLanguage {
  return uiLanguageOf(i18n);
}

/** Запись каталога для языка UI. */
export function getLanguageEntry(code: UiLanguage): (typeof languages)[number] {
  return languages.find((l) => l.code === code) ?? languages[0];
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: languages.map((l) => l.code),
    // `es-MX` / `en-GB` из детектора сводятся к `es` / `en`.
    load: 'languageOnly',
    fallbackLng,
    // Пустая строка в ресурсе = перевода нет → фолбэк по цепочке, а не пустота.
    returnEmptyString: false,
    defaultNS: 'translation',

    detection: {
      // Детектор — только первичная догадка для анонима; у залогиненного
      // язык перезаписывается из профиля (`useProfileLanguageSync`).
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React уже экранирует
    },

    react: {
      useSuspense: false,
    },
  });

// `<html lang>` следует за языком UI: скринридеры и переносы слов.
const syncDocumentLang = () => {
  if (typeof document !== 'undefined') document.documentElement.lang = currentUiLanguage();
};
syncDocumentLang();
i18n.on('languageChanged', syncDocumentLang);

export default i18n;
